<template>
  <div class="init-new-order" data-cy="order-type-form">
    <div class="form-cnt">
      <div class="form full-fields">
        <div v-if="loading" class="loader"></div>
        <div v-else class="form-grid">
          <div v-if="isSalesRep()" class="fields">
            <div class="field-new">
              <label for="init-new-order-contact">{{ $t('createOrdersPopup.contacts_title') }}</label>
              <select
                id="init-new-order-contact"
                v-model="initOrderModel.contactId"
                class="with-arrow"
                v-on:change="onChangeContact">
                <option disabled selected value="">-</option>
                <option v-for="contact in draftInitInfo?.userContacts" :key="contact.id" :value="contact.id">
                  {{ `${contact.firstName} ${contact.lastName}${hasBuyerFunction(contact) ? ' (Buyer)' : ''}` }}
                </option>
              </select>
              <div v-if="selectedContact" class="contact-info">
                <div>
                  <span class="title">{{ $t('createOrdersPopup.contacts_roles') }}</span>
                  <span class="description">{{ contactFunctions(selectedContact) }}</span>
                </div>
                <div>
                  <span class="title">{{ $t('createOrdersPopup.contacts_email') }}</span>
                  <span class="description">
                    <a :href="`mailto:${selectedContact.email}`">{{ selectedContact.email }} </a>
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div class="fields calendar-override">
            <div class="field-new">
              <label for="init-new-order-type-selector">{{ $t('createOrdersPopup.order_types_title') }}</label>
              <select
                id="init-new-order-type-selector"
                v-model="initOrderModel.orderTypeId"
                class="with-arrow"
                data-cy="order-type-selector"
                v-on:change="onChangeOrderType">
                <option
                  v-for="orderType in draftInitInfo?.orderTypes"
                  :key="orderType.type"
                  :value="orderType.type.toLocaleLowerCase()">
                  {{ $t('createOrdersPopup.order_type_pick_list_label_' + orderType.type.toLocaleLowerCase()) }}
                </option>
              </select>
            </div>
            <BackstageInputCalendar
              name="init-new-order-requested-date"
              v-model="initOrderModel.startDate"
              :label="$t('createOrdersPopup.shipping_date')"
              :disabled-dates="disabledDates"
              data-cy="requested-date-picker">
            </BackstageInputCalendar>
          </div>
        </div>
        <div class="buttons-cnt buttons-cnt-divisor full-width">
          <div class="fields">
            <div class="field">
              <BackstageButton
                :variant="ButtonVariant.SECONDARY"
                data-cy="cancel-button-cnt"
                data-cy-button="cancel-button"
                full-width
                v-on:click="close">
                {{ $t('createOrdersPopup.cancel_button') }}
              </BackstageButton>
            </div>
            <div class="field">
              <BackstageButton
                data-cy="create-button-cnt"
                data-cy-button="create-button"
                :disabled="!submitButtonEnabled()"
                full-width
                v-on:click="initOrder()">
                {{ $t('createOrdersPopup.create_button') }}
              </BackstageButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { InitOrderModel } from '../../models/init-order.model';
import { utilsMixins } from '../../mixins/utils.mixins';
import moment from 'moment';
import { SFContactModel } from '@/models/sf-contact.model';
import { ContactModel, DraftInitInfoDto, OrderTypeDto } from '@onrunning/backstage-models';
import { AddEditOrderBreadcrumbSteps } from '../../models/add-edit-order-breadcrumb-steps.enum';
import { GaCheckoutStepNames, GaCustomEvents } from '../../models/ga-custom-events.enum';
import { DraftsService } from '../../services/drafts.service';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import BackstageButton from '@/components/common/BackstageButton.vue';
import { useUserStore } from '../../stores/user';
import { storeToRefs } from 'pinia';
import { useFormat } from '../../composables/format';
import { ButtonVariant } from '@/models/button-variant.enum';

export default defineComponent({
  name: 'init-new-order',
  components: {
    BackstageInputCalendar,
    BackstageButton,
  },
  setup() {
    const { selectedAccountId } = storeToRefs(useUserStore());
    const { isSalesRep, hasBuyerFunction } = useUserStore();
    const { formatDateToIsoStringIgnoringTime } = useFormat();

    return {
      accountId: selectedAccountId,
      ButtonVariant,
      isSalesRep,
      hasBuyerFunction,
      formatDateToIsoStringIgnoringTime,
    };
  },
  data() {
    return {
      draftsService: new DraftsService(),
      selectedContact: undefined as ContactModel | undefined,
      initOrderModel: new InitOrderModel(),
      draftInitInfo: undefined as DraftInitInfoDto | undefined,
      disabledDates: (() => {
        return true;
      }) as (date: Date) => boolean,
      showMatrix: false,
      loading: true,
    };
  },
  mounted() {
    this.getInitInfo();

    this.addCustomGaEvent({
      event: GaCustomEvents.CheckoutStep1,
      checkout_step_name: GaCheckoutStepNames.SelectOrderType,
    });
  },
  methods: {
    ...utilsMixins,
    async getInitInfo() {
      this.draftInitInfo = (await this.draftsService.getInitInfo(this.accountId)).model;

      if (!this.draftInitInfo) {
        return this.$snotify.error(this.$t('global.error'));
      }

      this.initOrderModel.orderTypeId = this.draftInitInfo.orderTypes[0].type.toLocaleLowerCase();
      this.initOrderModel.startDate = moment(this.draftInitInfo.orderTypes[0].defaultDate).toDate();
      this.disableDates(this.draftInitInfo.orderTypes[0].startDate);

      this.loading = false;
    },
    contactFunctions(contact: SFContactModel): string {
      if (contact.userFunction) {
        return contact.userFunction.join(', ');
      } else {
        return this.$t('createOrdersPopup.contact_has_no_roles').toString();
      }
    },
    onChangeOrderType() {
      const selectedOrderType = this.draftInitInfo?.orderTypes.find(
        (type) => type.type.toLocaleLowerCase() === this.initOrderModel.orderTypeId,
      ) as OrderTypeDto;

      this.initOrderModel.startDate = moment(selectedOrderType.defaultDate).toDate();
      this.disableDates(selectedOrderType.startDate);
    },
    onChangeContact() {
      this.selectedContact = this.draftInitInfo?.userContacts.find(
        (contact) => contact.id === this.initOrderModel.contactId,
      );
    },
    submitButtonEnabled(): boolean {
      return !!this.initOrderModel.orderTypeId && (!this.isSalesRep() || !!this.initOrderModel.contactId);
    },
    initOrder() {
      this.$emit('close');
      let url = `/orders/create/${
        this.initOrderModel.orderTypeId
      }/${this.formatDateToIsoStringIgnoringTime(this.initOrderModel.startDate)}`;

      if (this.initOrderModel.contactId) {
        url += `/${this.initOrderModel.contactId}`;
      }

      url += `/${AddEditOrderBreadcrumbSteps.productsModelsSelection}`;

      this.$router.push(url);
    },
    disableDates(date: string) {
      this.disabledDates = (target: Date) => {
        return target < moment(date).toDate();
      };
    },
    close() {
      this.$emit('close');
    },
  },
});
</script>
<style scoped lang="scss">
.init-new-order {
  .form-cnt {
    .loader {
      width: 532px;
    }
    .form-grid {
      display: grid;
      gap: 10px;
    }

    .contact-info {
      padding: 5px 10px;
      margin-top: 5px;
      background: var(--read-only-field-bg-color);
      color: var(--primary-text-bold-color);

      div {
        margin-bottom: 2px;
        display: flex;
        span.title {
          width: 50px;
        }
        span.description {
          font-weight: 600;
        }
      }
    }
  }

  .calendar-override:deep() {
    .dp__menu {
      position: fixed !important;
    }
    .dp--menu-wrapper {
      // needs this given .popup-cnt .popup .popup-content { overflow: auto } not working on safari
      position: static;
    }
  }
}
</style>
